if (!customElements.get('pdp-gallery')) {
  customElements.define(
    'pdp-gallery',
    class PdpGallery extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-gallery-track]');
        if (!this.track) return;
        this.items = [...this.track.querySelectorAll('[data-gallery-item]')];
        this.thumbs = [...this.querySelectorAll('[data-thumb-media-id]')];

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.scrollToMedia(thumb.dataset.thumbMediaId))
        );

        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) this.setActive(entry.target.dataset.mediaId);
            }
          },
          // Center-band detection: tall images never hit a high visibility
          // threshold, so activate whichever item crosses the viewport middle.
          { rootMargin: '-40% 0px -40% 0px', threshold: 0 }
        );
        this.items.forEach((item) => this.observer.observe(item));

        this.counterWindow = this.querySelector('[data-gallery-counter-window]');
        this.counterIndex = 0;
        this.stackedQuery = window.matchMedia('(min-width: 64rem)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
        if (this.counterWindow) {
          // Mobile counts from scrollLeft: the observer is ambiguous with two slides half in view.
          this.onTrackScroll = () => {
            if (this.stackedQuery.matches) return;
            const width = this.track.clientWidth;
            if (width) this.updateCounter(Math.round(Math.abs(this.track.scrollLeft) / width));
          };
          this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });
        }

        this.onVariantChange = (event) => {
          if (event.detail.sectionId !== this.dataset.section) return;
          const id = event.detail.variant?.featured_media?.id;
          if (id) this.scrollToMedia(id);
        };
        document.addEventListener('variant:change', this.onVariantChange);

        this.progressFill = this.querySelector('[data-gallery-progress-fill]');
        if (this.progressFill) {
          this.updateProgress = () => {
            const max = this.track.scrollWidth;
            if (!max) return;
            const progress = Math.min(1, (Math.abs(this.track.scrollLeft) + this.track.clientWidth) / max);
            // scale-x-0 is the standalone `scale` property, which composes with
            // `transform`, so the override must use `scale` too.
            this.progressFill.style.scale = `${progress} 1`;
          };
          this.track.addEventListener('scroll', this.updateProgress, { passive: true });
          this.track.addEventListener('scrollend', this.updateProgress);
          window.addEventListener('resize', this.updateProgress);
          this.updateProgress();
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        document.removeEventListener('variant:change', this.onVariantChange);
        if (this.updateProgress) {
          window.removeEventListener('resize', this.updateProgress);
        }
      }

      setActive(id) {
        if (this.counterWindow && this.stackedQuery.matches) {
          const index = this.items.findIndex((item) => item.dataset.mediaId === String(id));
          if (index !== -1) this.updateCounter(index);
        }
        this.thumbs.forEach((thumb) =>
          thumb.toggleAttribute('data-active', thumb.dataset.thumbMediaId === String(id))
        );
        // Some browsers skimp on scroll events for snap carousels; the observer always fires.
        this.updateProgress?.();
      }

      updateCounter(index) {
        const clamped = Math.max(0, Math.min(index, this.items.length - 1));
        if (clamped === this.counterIndex) return;
        const direction = clamped > this.counterIndex ? 1 : -1;
        this.counterIndex = clamped;

        const current = this.counterWindow.lastElementChild;
        const next = document.createElement('span');
        next.textContent = clamped + 1;
        this.counterWindow.append(next);

        if (this.reducedMotion.matches) {
          current.remove();
          return;
        }
        const timing = { duration: 350, easing: 'cubic-bezier(0.2, 0.7, 0.2, 1)' };
        current
          .animate([{ transform: 'translateY(0)' }, { transform: `translateY(${-100 * direction}%)` }], timing)
          .finished.then(() => current.remove(), () => current.remove());
        next.animate([{ transform: `translateY(${100 * direction}%)` }, { transform: 'translateY(0)' }], timing);
      }

      scrollToMedia(id) {
        const item = this.items.find((el) => el.dataset.mediaId === String(id));
        if (!item) return;
        const stacked = window.matchMedia('(min-width: 64rem)').matches;
        if (stacked && window.lenis) {
          // Native smooth scrolling would fight Lenis; pass scroll-margin-top as
          // a negative offset to keep the nav clearance.
          const offset = -parseFloat(getComputedStyle(item).scrollMarginTop || '0');
          window.lenis.scrollTo(item, { offset });
        } else {
          item.scrollIntoView({
            behavior: 'smooth',
            block: stacked ? 'start' : 'nearest',
            inline: 'center',
          });
        }
      }
    }
  );
}
